import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingSimpleNav } from '../../components/MarketingSimpleNav';
import { PortalFooter } from '../../components/Footer';
import {
  YOUTUBE_CHANNEL_VIDEOS_URL,
  YOUTUBE_TUTORIAL_PAGE_PLAY_EMBED_URL,
  YOUTUBE_TUTORIAL_PLAY_EMBED_URL,
  YOUTUBE_TUTORIAL_THUMBNAIL_SIZES,
  YOUTUBE_TUTORIAL_THUMBNAIL_SRCSET,
  YOUTUBE_TUTORIAL_THUMBNAIL_URL,
} from '../landing/tutorialContent';

const TutorialsPage: React.FC = () => {
  const [videoShouldMount, setVideoShouldMount] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let timer: number | undefined;
    const mountAfterPageLoad = () => {
      timer = window.setTimeout(() => setVideoShouldMount(true), 1200);
    };

    if (document.readyState === 'complete') {
      mountAfterPageLoad();
    } else {
      window.addEventListener('load', mountAfterPageLoad, { once: true });
    }

    return () => {
      window.removeEventListener('load', mountAfterPageLoad);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <MarketingSimpleNav />
      <main style={{
        minHeight: '100vh',
        color: '#f8fafc',
        background:
          'radial-gradient(circle at 20% 0%, rgba(45,212,191,0.16), transparent 30%), radial-gradient(circle at 86% 10%, rgba(236,72,153,0.12), transparent 26%), linear-gradient(135deg, #05050a 0%, #0b1020 52%, #030712 100%)',
      }}>
        <section className="section" style={{ paddingTop: 92 }}>
          <div className="container section-center">
            <a
              href={YOUTUBE_CHANNEL_VIDEOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                marginBottom: 18,
                padding: '8px 12px',
                borderRadius: 999,
                border: '1px solid rgba(255,0,51,0.34)',
                background: 'rgba(255,0,51,0.08)',
                color: '#fecdd3',
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textDecoration: 'none',
              }}
            >
              <span style={{
                display: 'inline-grid',
                placeItems: 'center',
                width: 22,
                height: 16,
                borderRadius: 5,
                background: '#ff0033',
                color: '#fff',
                fontSize: 10,
                paddingLeft: 1,
              }}>
                ▶
              </span>
              Watch on YouTube
            </a>

            <h1 style={{
              maxWidth: 820,
              margin: '0 auto 12px',
              fontSize: 'clamp(2rem, 5vw, 3.3rem)',
              lineHeight: 0.98,
              letterSpacing: '-0.055em',
              textWrap: 'balance',
            }}>
              Learn how to install agentOmatic and start Agentic Engineering.
            </h1>
            <p className="section-subtitle" style={{ maxWidth: 760 }}>
              See how to download agentOmatic, set up your workspace, run AI agents, and move from idea to working product with more control.
            </p>

            <div style={{
              maxWidth: 980,
              margin: '30px auto 26px',
              borderRadius: 24,
              padding: 8,
              background: 'linear-gradient(135deg, rgba(45,212,191,0.34), rgba(192,132,252,0.20), rgba(236,72,153,0.16))',
              boxShadow: '0 28px 95px rgba(0,0,0,0.58)',
            }}>
              <div style={{
                position: 'relative',
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                borderRadius: 18,
                background: '#020617',
                border: '1px solid rgba(226,232,240,0.12)',
              }}>
                <img
                  src={YOUTUBE_TUTORIAL_THUMBNAIL_URL}
                  srcSet={YOUTUBE_TUTORIAL_THUMBNAIL_SRCSET}
                  sizes={YOUTUBE_TUTORIAL_THUMBNAIL_SIZES}
                  alt="agentOmatic tutorial thumbnail"
                  loading="eager"
                  decoding="async"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: 0.42,
                    filter: 'saturate(1.04) contrast(1.03)',
                  }}
                />
                {videoShouldMount && (
                  <iframe
                    title="agentOmatic tutorial video"
                    src={YOUTUBE_TUTORIAL_PAGE_PLAY_EMBED_URL || YOUTUBE_TUTORIAL_PLAY_EMBED_URL}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                    }}
                  />
                )}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 14,
              maxWidth: 960,
              margin: '0 auto 28px',
              textAlign: 'left',
            }}>
              {[
                {
                  title: 'Install the desktop app.',
                  body: 'Download agentOmatic for Linux, Windows, or macOS and get into a real workspace instead of another browser tab.',
                },
                {
                  title: 'Run agents with structure.',
                  body: 'Name the agent, assign the role, keep the terminal visible, and stop guessing which CLI owns which task.',
                },
                {
                  title: 'Ship with control.',
                  body: 'Use tutorials and support to build faster while keeping taste, standards, and production quality in your hands.',
                },
              ].map((item) => (
                <article key={item.title} style={{
                  padding: 20,
                  borderRadius: 18,
                  border: '1px solid rgba(45,212,191,0.20)',
                  background: 'linear-gradient(180deg, rgba(15,23,42,0.72), rgba(15,23,42,0.42))',
                }}>
                  <h2 style={{ margin: '0 0 8px', fontSize: '1rem' }}>{item.title}</h2>
                  <p style={{ margin: 0, color: '#a7b2c6', lineHeight: 1.55, fontSize: '0.92rem' }}>
                    {item.body}
                  </p>
                </article>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
              <a className="btn btn-primary" href="/portal-guest">Free Download</a>
              <a className="btn btn-secondary" href={YOUTUBE_CHANNEL_VIDEOS_URL} target="_blank" rel="noopener noreferrer">
                More videos
              </a>
            </div>

            <details style={{
              maxWidth: 820,
              margin: '0 auto',
              padding: '16px 18px',
              borderRadius: 14,
              border: '1px solid rgba(148,163,184,0.18)',
              background: 'rgba(15,23,42,0.38)',
              textAlign: 'left',
              color: '#a7b2c6',
            }}>
              <summary style={{ cursor: 'pointer', color: '#e5e7eb', fontWeight: 850 }}>
                Advanced setup guides, stashed for later
              </summary>
              <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                <Link to="/tutorials/quickstart-engineer" style={{ color: '#67e8f9' }}>Engineer quickstart</Link>
                <Link to="/tutorials/quickstart-beginner" style={{ color: '#67e8f9' }}>Beginner quickstart</Link>
                <Link to="/tutorials/setup-claude-code" style={{ color: '#67e8f9' }}>Claude Code and CLI setup</Link>
                <Link to="/tutorials/setup-openclaw" style={{ color: '#67e8f9' }}>OpenClaw setup notes</Link>
              </div>
            </details>
          </div>
        </section>
      </main>
      <PortalFooter />
    </>
  );
};

export default TutorialsPage;
